import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy - Kecly"
        description="Learn how Kecly protects your privacy and handles your personal information. Read our comprehensive privacy policy."
        keywords="privacy policy, data protection, Kecly privacy"
      />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-8">
              Privacy Policy
            </h1>
            
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-muted-foreground mb-6">
                Last updated: December 2024
              </p>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Introduction</h2>
                <p className="text-muted-foreground mb-4">
                  <strong>Welcome to Kecly's Privacy Policy!</strong> Your privacy is important to us. This policy explains how we collect, use, and protect your personal information when you use our services or visit our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Information We Collect</h2>
                <h3 className="font-orbitron font-medium text-lg mb-3">Personal Information</h3>
                <p className="text-muted-foreground mb-4">
                  We may collect personally identifiable information, such as your name, email address, phone number, and company information when you:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Fill out contact forms on our website</li>
                  <li>Request quotes or consultations</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Apply for job positions</li>
                  <li>Engage with our customer support</li>
                </ul>

                <h3 className="font-orbitron font-medium text-lg mb-3">Non-Personal Information</h3>
                <p className="text-muted-foreground mb-4">
                  We may collect non-personal information about your visits to our website, including browser type, operating system, referring URLs, and pages viewed.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">How We Use Your Information</h2>
                <p className="text-muted-foreground mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Process service requests and deliver our services</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                  <li>Protect against fraud and unauthorized access</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Information Sharing</h2>
                <p className="text-muted-foreground mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>With your explicit consent</li>
                  <li>To trusted service providers who assist us in operating our business</li>
                  <li>When required by law or to protect our legal rights</li>
                  <li>In connection with a business transaction (merger, acquisition, etc.)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Data Security</h2>
                <p className="text-muted-foreground mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Your Rights</h2>
                <p className="text-muted-foreground mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                  <li>Right to access your personal data</li>
                  <li>Right to rectify inaccurate information</li>
                  <li>Right to erase your personal data</li>
                  <li>Right to restrict processing</li>
                  <li>Right to data portability</li>
                  <li>Right to object to processing</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Cookies</h2>
                <p className="text-muted-foreground mb-4">
                  Our website uses cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, though this may affect the functionality of our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Changes to This Policy</h2>
                <p className="text-muted-foreground mb-4">
                  Yes, Kecly may update this policy periodically. We will notify you of significant changes via email or website notice. We will also post the new Privacy Policy on this page and update the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-orbitron font-semibold text-2xl text-primary mb-4">Contact Us</h2>
                <p className="text-muted-foreground mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="text-muted-foreground">
                  <p>Email: info@kecly.com</p>
                  <p>Phone: +971-58-248-1443</p>
                  <p>Address: Almawarid Building 209, Industrial Area 3, Sharjah, UAE</p>
                </div>
              </section>

              <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                  <WebDesignLocations />
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
